import React, { useEffect, useMemo, useState } from 'react';
import { createInventoryIssueReceipt, listInventoryIssueReceipts, deleteInventoryIssueReceipt } from '../api/inventoryIssueApi';
import SearchableSelect from './SearchableSelect';

const DRESS_TYPES = [
  { value: 'Shirt', label: 'Shirt' },
  { value: 'Pant', label: 'Pant' },
  { value: 'Skirt', label: 'Skirt' },
  { value: 'Sweater', label: 'Sweater' },
  { value: 'Tie', label: 'Tie' },
  { value: 'Belt', label: 'Belt' },
  { value: 'Blazer', label: 'Blazer' },
];

const DRESS_SIZES = ['20', '22', '24', '26', '28', '30', '32', '34', '36', '38', '40', 'S', 'M', 'L', 'XL'].map((s) => ({
  value: s,
  label: s,
}));

const emptyLine = () => ({
  type: 'book',
  studentId: '',
  qty: '1',
  class: '',
  subject: '',
  dressType: 'Shirt',
  size: '28',
});

export default function InventoryIssueSection({ students, books, dresses, user, profile, setMsg, setErr, onIssued, allowDelete = false }) {
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState([emptyLine()]);
  const [issues, setIssues] = useState([]);
  const [loadingIssues, setLoadingIssues] = useState(false);

  const studentOpts = useMemo(
    () =>
      students.map((s) => ({
        value: s.studentId,
        label: `${s.studentName} · adm ${s.admno} · class ${s.class || '—'}`,
      })),
    [students]
  );

  const bookOpts = useMemo(
    () =>
      books.map((b) => ({
        value: b.id,
        label: `${b.subject} · class ${b.class} · stock ${b.count}`,
        class: b.class,
        subject: b.subject,
        count: b.count,
      })),
    [books]
  );

  const dressOpts = useMemo(
    () =>
      dresses.map((d) => ({
        value: d.id,
        label: `${d.dressType} · size ${d.size} · stock ${d.count}`,
        dressType: d.dressType,
        size: d.size,
        count: d.count,
      })),
    [dresses]
  );

  const loadIssues = async () => {
    setLoadingIssues(true);
    try {
      setIssues(await listInventoryIssueReceipts());
    } finally {
      setLoadingIssues(false);
    }
  };

  useEffect(() => {
    loadIssues().catch(() => setIssues([]));
  }, []);

  const updateLine = (idx, patch) => {
    setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMsg('');
    setErr('');
    try {
      const payloadLines = lines.map((line) => {
        const student = students.find((s) => s.studentId === line.studentId);
        if (!student) throw new Error('Select a student on every line.');
        const qty = Number(line.qty) || 1;
        if (line.type === 'book') {
          const book = bookOpts.find((b) => b.class === line.class && b.subject === line.subject);
          if (!line.class || !line.subject) throw new Error('Select book from stock.');
          if (book && qty > book.count) throw new Error(`Not enough stock for ${line.subject}.`);
          return {
            type: 'book',
            studentId: student.studentId,
            studentName: student.studentName,
            qty,
            class: line.class,
            subject: line.subject,
          };
        }
        const dress = dressOpts.find((d) => d.dressType === line.dressType && d.size === line.size);
        if (!line.dressType || !line.size) throw new Error('Select dress type and size.');
        if (dress && qty > dress.count) throw new Error(`Not enough dress stock (${line.dressType} ${line.size}).`);
        return {
          type: 'dress',
          studentId: student.studentId,
          studentName: student.studentName,
          qty,
          dressType: line.dressType,
          size: line.size,
        };
      });

      const result = await createInventoryIssueReceipt({
        lines: payloadLines,
        notes,
        createdByUid: user?.uid,
        createdByName: profile?.name,
      });
      setMsg(`Issue receipt created (${result.lineCount} line(s)). Stock updated.`);
      setLines([emptyLine()]);
      setNotes('');
      await loadIssues();
      if (onIssued) await onIssued();
    } catch (ex) {
      setErr(ex.message);
    }
  };

  return (
    <>
      <div className="mps-card">
        <h2>Issue books / dress to students</h2>
        <p className="mps-muted" style={{ marginTop: 0 }}>
          Creates an issue receipt and <strong>reduces inventory stock</strong> automatically.
        </p>
        {books.length === 0 && dresses.length === 0 ? (
          <p className="mps-msg mps-msg-info">
            Add book or dress stock first (Book inventory / Dress inventory tabs), then return here to issue items to students.
          </p>
        ) : null}
        <form onSubmit={handleSubmit}>
          {lines.map((line, idx) => (
            <div key={idx} className="mps-issue-line">
              <div className="mps-grid">
                <div className="mps-field">
                  <label>Type</label>
                  <SearchableSelect
                    value={line.type}
                    onChange={(v) => updateLine(idx, { type: v })}
                    options={[
                      { value: 'book', label: 'Book' },
                      { value: 'dress', label: 'Dress' },
                    ]}
                  />
                </div>
                <div className="mps-field">
                  <label>Student</label>
                  <SearchableSelect
                    value={line.studentId}
                    onChange={(v) => updateLine(idx, { studentId: v })}
                    options={studentOpts}
                    placeholder="Select student…"
                  />
                </div>
                <div className="mps-field">
                  <label>Qty</label>
                  <input type="number" min="1" value={line.qty} onChange={(e) => updateLine(idx, { qty: e.target.value })} />
                </div>
              </div>
              {line.type === 'book' ? (
                <div className="mps-grid" style={{ marginTop: 8 }}>
                  <div className="mps-field">
                    <label>Book (from stock)</label>
                    <SearchableSelect
                      value={bookOpts.find((b) => b.class === line.class && b.subject === line.subject)?.value || ''}
                      onChange={(id) => {
                        const b = bookOpts.find((x) => x.value === id);
                        if (b) updateLine(idx, { class: b.class, subject: b.subject });
                      }}
                      options={bookOpts}
                      placeholder="Select book…"
                    />
                  </div>
                </div>
              ) : (
                <div className="mps-grid" style={{ marginTop: 8 }}>
                  <div className="mps-field">
                    <label>Dress type</label>
                    <SearchableSelect
                      value={line.dressType}
                      onChange={(v) => updateLine(idx, { dressType: v })}
                      options={DRESS_TYPES}
                    />
                  </div>
                  <div className="mps-field">
                    <label>Size</label>
                    <SearchableSelect
                      value={line.size}
                      onChange={(v) => updateLine(idx, { size: v })}
                      options={DRESS_SIZES}
                    />
                  </div>
                </div>
              )}
              {lines.length > 1 ? (
                <button
                  type="button"
                  className="mps-btn mps-btn-sm mps-btn-danger"
                  style={{ marginTop: 8 }}
                  onClick={() => setLines((p) => p.filter((_, i) => i !== idx))}
                >
                  Remove line
                </button>
              ) : null}
            </div>
          ))}
          <button type="button" className="mps-btn mps-btn-secondary mps-btn-sm" onClick={() => setLines((p) => [...p, emptyLine()])}>
            + Add line
          </button>
          <div className="mps-field" style={{ marginTop: 12 }}>
            <label>Notes</label>
            <input value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>
          <button type="submit" className="mps-btn" style={{ marginTop: 12 }}>
            Create issue receipt
          </button>
        </form>
      </div>
      <div className="mps-card">
        <h2>Recent issue receipts</h2>
        {loadingIssues ? (
          <p>Loading…</p>
        ) : issues.length === 0 ? (
          <p className="mps-muted">No issues yet.</p>
        ) : (
          <table className="mps-table">
            <thead>
              <tr>
                <th>Receipt</th>
                <th>Date</th>
                <th>Items</th>
                {allowDelete ? <th>Actions</th> : null}
              </tr>
            </thead>
            <tbody>
              {issues.slice(0, 20).map((r) => (
                <tr key={r.id}>
                  <td>{r.receiptNo}</td>
                  <td>{r.date}</td>
                  <td>
                    {(r.lines || [])
                      .map((l) =>
                        l.type === 'book'
                          ? `${l.studentName}: ${l.subject}×${l.qty}`
                          : `${l.studentName}: ${l.dressType} ${l.size}×${l.qty}`
                      )
                      .join('; ')}
                  </td>
                  {allowDelete ? (
                    <td>
                      <button
                        type="button"
                        className="mps-btn mps-btn-sm mps-btn-danger"
                        onClick={async () => {
                          if (!window.confirm(`Delete issue receipt ${r.receiptNo}? Stock will be restored.`)) return;
                          try {
                            await deleteInventoryIssueReceipt(r.id);
                            setMsg('Issue receipt deleted. Stock restored.');
                            await loadIssues();
                            if (onIssued) await onIssued();
                          } catch (ex) {
                            setErr(ex.message);
                          }
                        }}
                      >
                        Delete
                      </button>
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}
